// ============================================
// VFXMultiSystemRenderer - Multi-System VFX Graphs
// ============================================
// Coordinates multiple particle systems with shared GPU event buffer

import type { IRenderContext } from '@engine/render2d';
import type { BufferHandle, TextureHandle } from '@engine/render2d';
import { VFXSystemRenderer, type CompiledSystemData, type SharedVFXResources } from './VFXSystemRenderer';
import { tweakValueToFloats } from './compiler/codegen/Tweaks';
import { VFXNotSupportedError } from './VFXTypes';
import { VFX_EVENT_COUNT_BUFFER_SIZE } from './VFXBindings';
import { GPU_EVENT_LAYOUT } from './compiler/Layouts';
import { IVFXRenderer, type LightEmittingSystemInfo } from '../runtime';

/**
 * Compiled multi-system VFX graph data.
 */
export interface CompiledMultiSystemData {
  /** Graph name */
  name: string;
  /** All systems in execution order */
  systems: CompiledSystemData[];
  /** Shared particle struct WGSL */
  particleStruct: string;
  /** Warnings */
  warnings?: string[];
}

/**
 * Options for VFXMultiSystemRenderer.
 */
export interface VFXMultiSystemRendererOptions {
  /** Workgroup size */
  workgroupSize?: number;
  /** Debug label */
  label?: string;
  /** Max events in buffer */
  maxEvents?: number;
  /** Default texture to use when no texture is bound (e.g., from TextureManager.getBuiltin('white')) */
  defaultTexture?: TextureHandle;
}

/**
 * VFXMultiSystemRenderer - renders a multi-system VFX graph.
 * 
 * Systems are executed in order:
 * 1. First systems (direct spawn, may output events)
 * 2. Event-driven systems (read events, spawn particles)
 * 
 * All systems share an event buffer for GPU-side communication.
 * 
 * @example
 * ```ts
 * // Firework: launcher → burst
 * const firework = new VFXMultiSystemRenderer(ctx, compiledData);
 * 
 * // Each frame
 * firework.update(dt);
 * firework.render(viewProjection);
 * ```
 */
/**
 * Per-system spawn state for all spawn types.
 * Follows Unity VFX Graph accumulator model.
 */
interface SystemSpawnState {
  /** Spawn type matches CompiledSystemData.spawnType */
  type: 'rate' | 'variable_rate' | 'single_burst' | 'periodic_burst' | 'burst' | 'none';
  
  /** 
   * Spawn accumulator - fractional particles carry over between frames.
   * spawnCount = floor(accumulator), accumulator -= floor(accumulator) 
   */
  accumulator: number;
  
  /** For constant rate spawn */
  rate?: number;
  
  /** For variable rate spawn (random between min/max each frame) */
  variableRate?: {
    rateMin: number;
    rateMax: number;
  };
  
  /** For burst-based spawn types */
  burst?: {
    /** Particles per burst */
    count: number;
    /** Remaining bursts (-1 = infinite for periodic_burst) */
    remaining: number;
    /** Interval between bursts (seconds) */
    interval: number;
    /** Initial delay before first burst (seconds) */
    delay: number;
    /** Time until next burst (counts down) */
    timer: number;
    /** Whether initial delay has passed */
    delayPassed: boolean;
  };
  
  /** Original remaining count for re-arming burst loops */
  _originalRemaining?: number;
}

export class VFXMultiSystemRenderer implements IVFXRenderer {
  private ctx: IRenderContext;
  private label: string;
  
  // Systems
  private systems: VFXSystemRenderer[] = [];
  private directSpawnSystems: VFXSystemRenderer[] = [];
  private eventDrivenSystems: VFXSystemRenderer[] = [];
  
  // Per-system spawn state
  private systemSpawnStates: Map<VFXSystemRenderer, SystemSpawnState> = new Map();
  
  // Shared GPU Resources
  private sharedResources: SharedVFXResources | null = null;
  private eventBuffer: BufferHandle | null = null;
  private eventCountBuffer: BufferHandle | null = null;
  private indirectBuffer: BufferHandle | null = null;
  
  private _maxEvents: number;
  private _isDestroyed: boolean = false;
  private _time: number = 0;
  private _emitterX: number = 0;
  private _emitterY: number = 0;
  private _emitterScaleX: number = 1;
  private _emitterScaleY: number = 1;
  private _defaultTexture: TextureHandle | undefined;
  private _loop: boolean = false;
  private _lastSpawnTime: number = 0;
  private _spawnsExhausted: boolean = false;
  
  constructor(
    ctx: IRenderContext,
    data: CompiledMultiSystemData,
    options: VFXMultiSystemRendererOptions = {}
  ) {
    this.ctx = ctx;
    this.label = options.label ?? `vfx-multi-${data.name}`;
    this._maxEvents = options.maxEvents ?? 1024;
    this._defaultTexture = options.defaultTexture;
    
    if (!ctx.supportsCompute) {
      throw new VFXNotSupportedError('VFX Multi-System requires WebGPU compute shaders.');
    }
    
    this._initializeSharedResources();
    this._createSystems(data.systems, options.workgroupSize);
    
    if (data.warnings?.length) {
      console.warn(`[VFXMultiSystem:${this.label}] Warnings:`, data.warnings);
    }
  }
  
  /**
   * Set loop mode. When true, finite spawn systems (single_burst, burst)
   * will automatically reset and replay once all their spawns are exhausted.
   */
  setLoop(loop: boolean): void {
    this._loop = loop;
  }

  /**
   * Returns true when the effect is fully finished:
   * spawning is exhausted, loop is off, and enough time has passed for all particles to die.
   * Uses a conservative 10-second window after the last spawn.
   */
  get isComplete(): boolean {
    if (this._loop) return false;
    if (!this._spawnsExhausted) return false;
    return (this._time - this._lastSpawnTime) > 10;
  }

  /**
   * Set emitter world position for all systems.
   */
  setPosition(x: number, y: number): void {
    this._emitterX = x;
    this._emitterY = y;
    for (const sys of this.systems) {
      sys.setPosition(x, y);
    }
  }
  
  /**
   * Set emitter scale for all systems.
   */
  setScale(x: number, y: number): void {
    this._emitterScaleX = x;
    this._emitterScaleY = y;
    for (const sys of this.systems) {
      sys.setScale(x, y);
    }
  }
  
  /**
   * Editor live-preview: write a tweak value for a graph port across all
   * systems that carry a binding for it (a shared operator's input may feed
   * several systems). No-op for literal-baked systems.
   * @returns true if at least one system had a binding for the port.
   */
  setTweakValue(nodeId: string, portId: string, value: unknown): boolean {
    let applied = false;
    for (const sys of this.systems) {
      const binding = sys.tweaks?.find(t => t.nodeId === nodeId && t.portId === portId);
      if (binding) {
        sys.setTweak(binding.slot, tweakValueToFloats(value, binding.type));
        applied = true;
      }
    }
    return applied;
  }

  /**
   * Set a texture for a variable slot across all systems.
   * Each system will use the texture if its shader expects it at that slot.
   * @param slot - Texture slot index (0-7)
   * @param texture - Texture handle (or null to clear)
   */
  setTexture(slot: number, texture: import('@engine/render2d').TextureHandle | null): void {
    for (const sys of this.systems) {
      sys.setTexture(slot, texture);
    }
  }
  
  /**
   * Set the default fallback texture used when no texture is bound to a slot.
   * Applies to all systems in this multi-system renderer.
   * @param texture - Texture handle to use as default
   */
  setDefaultTexture(texture: import('@engine/render2d').TextureHandle): void {
    for (const sys of this.systems) {
      sys.setDefaultTexture(texture);
    }
  }
  
  /**
   * Get the default texture from the first system (all systems share the same default).
   */
  getDefaultTexture(): import('@engine/render2d').TextureHandle | null {
    return this.systems[0]?.getDefaultTexture() ?? null;
  }
  
  /**
   * Queue particles to spawn in direct-spawn systems.
   */
  spawn(count: number, systemId?: string): void {
    if (systemId) {
      const sys = this.systems.find(s => s.systemId === systemId);
      if (sys && !sys.isEventDriven) {
        sys.spawn(count);
      }
    } else {
      // Spawn in first direct-spawn system
      if (this.directSpawnSystems.length > 0) {
        this.directSpawnSystems[0].spawn(count);
      }
    }
  }

  /**
   * Get all systems that emit light (have vfx.output.light node).
   * Used by VFXLightInjectPass to read particle buffers.
   */
  getLightEmittingSystems(): readonly LightEmittingSystemInfo[] {
    const result: LightEmittingSystemInfo[] = [];
    for (const sys of this.systems) {
      if (!sys.emitsLight) continue;
      const buf = sys.particleBufferHandle;
      const cfg = sys.lightConfig;
      if (!buf || !cfg) continue;
      result.push({
        particleBuffer: buf,
        maxParticles: sys.maxParticles,
        emitterX: this._emitterX,
        emitterY: this._emitterY,
        emitterScaleX: this._emitterScaleX,
        emitterScaleY: this._emitterScaleY,
        lightConfig: cfg,
      });
    }
    return result;
  }
  
  /**
   * Update all systems in order.
   * 
   * WebGPU synchronization strategy:
   * - Clear eventCount at END of frame (after Event Systems have read it)
   * - This ensures events written by Main System are visible to Event System in next frame
   * - Creates 1-frame latency but guarantees correct data visibility
   */
  update(deltaTime: number): void {
    if (this._isDestroyed) return;
    
    // Note: No outer debug group here - flushCompute() submits command buffers
    // which would cause push/pop mismatch. Individual system passes have their
    // own debug groups instead.
    
    this._time += deltaTime;

    // Handle rate-based and burst spawning for each direct-spawn system
    for (const sys of this.directSpawnSystems) {
      const state = this.systemSpawnStates.get(sys);
      if (!state) continue;
      
      let toSpawn = 0;
      
      switch (state.type) {
        case 'rate': {
          // Constant rate: spawnCount += rate * dt
          if (state.rate && state.rate > 0) {
            state.accumulator += state.rate * deltaTime;
          }
          break;
        }
        
        case 'variable_rate': {
          // Variable rate: random rate between rateMin and rateMax each frame
          // Following Unity VFX Graph "Variable Rate Random" behavior
          if (state.variableRate) {
            const { rateMin, rateMax } = state.variableRate;
            // Pick random rate between min and max each frame
            const rate = rateMin + Math.random() * (rateMax - rateMin);
            state.accumulator += rate * deltaTime;
          }
          break;
        }
        
        case 'single_burst': {
          // Single burst: spawn once after delay
          if (state.burst && state.burst.remaining > 0) {
            if (!state.burst.delayPassed) {
              state.burst.timer -= deltaTime;
              if (state.burst.timer <= 0) {
                state.burst.delayPassed = true;
              }
            }
            
            if (state.burst.delayPassed) {
              toSpawn = state.burst.count;
              state.burst.remaining = 0; // done
            }
          }
          break;
        }
        
        case 'periodic_burst': {
          // Periodic burst: spawn every interval (infinite)
          if (state.burst) {
            // Handle initial delay
            if (!state.burst.delayPassed) {
              state.burst.timer -= deltaTime;
              if (state.burst.timer <= 0) {
                state.burst.delayPassed = true;
                state.burst.timer = 0; // spawn immediately after delay
              }
            }
            
            if (state.burst.delayPassed) {
              state.burst.timer -= deltaTime;
              if (state.burst.timer <= 0) {
                toSpawn = state.burst.count;
                state.burst.timer = state.burst.interval; // reset for next burst
              }
            }
          }
          break;
        }
        
        case 'burst': {
          // Full burst: spawn count particles, repeat times, with delay then interval
          if (state.burst && state.burst.remaining !== 0) {
            // Handle initial delay
            if (!state.burst.delayPassed) {
              state.burst.timer -= deltaTime;
              if (state.burst.timer <= 0) {
                state.burst.delayPassed = true;
                state.burst.timer = 0; // spawn immediately after delay
              }
            }
            
            if (state.burst.delayPassed) {
              state.burst.timer -= deltaTime;
              if (state.burst.timer <= 0) {
                toSpawn = state.burst.count;
                
                // Decrement remaining (unless infinite)
                if (state.burst.remaining > 0) {
                  state.burst.remaining--;
                }
                
                // Reset timer for next burst (if any remaining)
                if (state.burst.remaining !== 0) {
                  state.burst.timer = state.burst.interval;
                }
              }
            }
          }
          break;
        }
      }
      
      // Handle accumulator-based spawning (rate and variable_rate)
      if (state.accumulator >= 1) {
        toSpawn += Math.floor(state.accumulator);
        state.accumulator -= Math.floor(state.accumulator);
      }
      
      // Clamp spawn count to system capacity (can't spawn more than maxParticles)
      if (toSpawn > 0) {
        toSpawn = Math.min(toSpawn, sys.maxParticles);
      }
      
      // Spawn particles
      if (toSpawn > 0) {
        sys.spawn(toSpawn);
        this._lastSpawnTime = this._time;
        this._spawnsExhausted = false;
      }
    }
    
    // Track spawn exhaustion for isComplete
    if (!this._loop && !this._spawnsExhausted) {
      let exhausted = true;
      for (const [, state] of this.systemSpawnStates) {
        if (state.type === 'rate' || state.type === 'variable_rate' || state.type === 'periodic_burst') {
          exhausted = false;
          break;
        }
        if ((state.type === 'single_burst' || state.type === 'burst') && state.burst && state.burst.remaining > 0) {
          exhausted = false;
          break;
        }
      }
      if (exhausted && this.systemSpawnStates.size > 0) {
        this._spawnsExhausted = true;
      }
    }

    // If looping, check if all finite-burst systems have exhausted their spawns.
    // If so, re-arm spawn states so they fire again. Existing particles continue
    // their lifecycle naturally — only spawn scheduling is restarted.
    if (this._loop) {
      let allExhausted = true;
      for (const [, state] of this.systemSpawnStates) {
        if (state.type === 'rate' || state.type === 'variable_rate' || state.type === 'periodic_burst') {
          // Continuous spawn types never exhaust
          allExhausted = false;
          break;
        }
        if (state.type === 'single_burst' || state.type === 'burst') {
          if (state.burst && state.burst.remaining > 0) {
            allExhausted = false;
            break;
          }
        }
      }
      if (allExhausted && this.systemSpawnStates.size > 0) {
        this._rearmSpawnStates();
      }
    }

    // Update direct-spawn systems first (they may produce events)
    // These write: eventCount, events[], position/velocity/color
    for (const sys of this.directSpawnSystems) {
      sys.update(deltaTime);
    }
    
    // CRITICAL: Flush and submit command buffer to ensure Main System writes
    // are visible to Event System. Without this, Event System may read stale
    // eventCount/events data due to GPU memory coherency issues.
    if (this.ctx.flushCompute) {
      this.ctx.flushCompute();
    }
    
    // Update event-driven systems (they read events from this frame's Main System)
    // Now eventCount and events[] contain data from Main System that just executed
    for (const sys of this.eventDrivenSystems) {
      sys.update(deltaTime);
    }
    
    // Ensure all compute work finished before clearing counter
    if (this.ctx.flushCompute) {
      this.ctx.flushCompute();
    }
    
    // After Event Systems have read the events, clear counter for next frame
    // This must be AFTER Event Systems read, BEFORE next frame's Main System writes
    this._clearEventCount();
    // Note: No flush needed after clearEventCount - CPU writeBuffer will be
    // included in next frame's command buffer automatically
  }
  
  /**
   * Render all systems.
   * @param viewProjection - View-projection matrix
   * @param ctx - Optional render context (uses stored ctx if not provided)
   * @param isHdrTarget - Whether rendering to an HDR target (rgba16float). Default false for screen.
   */
  render(viewProjection: Float32Array, ctx?: IRenderContext, isHdrTarget: boolean = false): void {
    if (this._isDestroyed) return;
    
    // Note: No debug group here - each system pushes its own after setRenderTarget
    // to avoid encoder mismatch issues
    
    // Render in reverse order (event-driven on top)
    for (let i = this.systems.length - 1; i >= 0; i--) {
      this.systems[i].render(viewProjection, ctx, isHdrTarget);
    }
  }
  
  /**
   * Reset all systems.
   */
  reset(): void {
    if (this._isDestroyed) return;
    
    this._time = 0;
    this._clearEventCount();
    
    // Reset spawn states for all direct-spawn systems
    for (const [, state] of this.systemSpawnStates) {
      state.accumulator = 0;
      
      if (state.burst) {
        // Reset burst state
        state.burst.timer = state.burst.delay > 0 ? state.burst.delay : 0;
        state.burst.delayPassed = state.burst.delay <= 0;
        
        // Reset remaining based on spawn type
        if (state.type === 'single_burst') {
          state.burst.remaining = 1;
        } else if (state.type === 'periodic_burst') {
          state.burst.remaining = -1; // infinite
        } else if (state.type === 'burst' && state._originalRemaining !== undefined) {
          state.burst.remaining = state._originalRemaining;
        }
      }
    }
    
    for (const sys of this.systems) {
      sys.reset();
    }
  }
  
  /**
   * Destroy all resources.
   */
  destroy(): void {
    if (this._isDestroyed) return;
    this._isDestroyed = true;
    
    // Destroy systems
    for (const sys of this.systems) {
      sys.destroy();
    }
    this.systems = [];
    this.directSpawnSystems = [];
    this.eventDrivenSystems = [];
    
    // Destroy shared resources
    if (this.eventCountBuffer) this.ctx.destroyBuffer(this.eventCountBuffer);
    if (this.eventBuffer) this.ctx.destroyBuffer(this.eventBuffer);
    if (this.indirectBuffer) this.ctx.destroyBuffer(this.indirectBuffer);
    
    this.sharedResources = null;
  }
  
  // ============================================
  // Private
  // ============================================
  
  private _initializeSharedResources(): void {
    // Event count buffer (single atomic u32) - shared between systems for event communication
    this.eventCountBuffer = this.ctx.createBuffer({
      size: VFX_EVENT_COUNT_BUFFER_SIZE,
      usage: ['storage', 'copy-dst', 'copy-src'],
      label: `${this.label}-eventcount`,
    });
    // Initialize to 0
    this.ctx.updateBuffer(this.eventCountBuffer, new Uint32Array([0]));
    
    // Event buffer
    this.eventBuffer = this.ctx.createBuffer({
      size: this._maxEvents * GPU_EVENT_LAYOUT.size,
      usage: ['storage', 'copy-dst'],
      label: `${this.label}-events`,
    });
    // Zero-initialize event buffer to prevent garbage reads
    this.ctx.updateBuffer(this.eventBuffer, new Float32Array(this._maxEvents * GPU_EVENT_LAYOUT.size / 4));
    
    // Indirect buffer
    this.indirectBuffer = this.ctx.createBuffer({
      size: 12,
      usage: ['storage', 'indirect', 'copy-dst'],
      label: `${this.label}-indirect`,
    });
    
    this.sharedResources = {
      eventBuffer: this.eventBuffer,
      eventCountBuffer: this.eventCountBuffer,
      indirectBuffer: this.indirectBuffer,
      maxEvents: this._maxEvents,
    };
  }
  
  private _createSystems(systemsData: CompiledSystemData[], workgroupSize?: number): void {
    for (const sysData of systemsData) {
      const sys = new VFXSystemRenderer(this.ctx, sysData, {
        workgroupSize,
        label: `${this.label}-${sysData.id}`,
        shared: this.sharedResources!,
        defaultTexture: this._defaultTexture,
      });
      
      this.systems.push(sys);
      
      if (sys.isEventDriven) {
        this.eventDrivenSystems.push(sys);
      } else {
        this.directSpawnSystems.push(sys);
        
        // Initialize spawn state based on spawn type
        const spawnType = sysData.spawnType ?? 'none';
        const spawnState: SystemSpawnState = {
          type: spawnType,
          accumulator: 0,
        };
        
        switch (spawnType) {
          case 'rate':
            spawnState.rate = sysData.spawnRate ?? 0;
            break;
            
          case 'variable_rate':
            spawnState.variableRate = sysData.variableRate;
            break;
            
          case 'single_burst':
            // Single burst: spawn once after delay
            if (sysData.burst) {
              spawnState.burst = {
                count: sysData.burst.count,
                remaining: 1,
                interval: 0,
                delay: sysData.burst.delay,
                timer: sysData.burst.delay, // start at delay
                delayPassed: sysData.burst.delay <= 0, // immediate if no delay
              };
              spawnState._originalRemaining = 1;
            }
            break;
            
          case 'periodic_burst':
            // Periodic burst: spawn every interval (infinite)
            if (sysData.burst) {
              const hasDelay = sysData.burst.delay > 0;
              spawnState.burst = {
                count: sysData.burst.count,
                remaining: -1, // infinite
                interval: sysData.burst.interval,
                delay: sysData.burst.delay,
                timer: hasDelay ? sysData.burst.delay : 0, // delay or spawn immediately
                delayPassed: !hasDelay,
              };
            }
            break;
            
          case 'burst':
            // Full burst: spawn count particles, repeat times, with delay then interval
            if (sysData.burst) {
              const hasDelay = sysData.burst.delay > 0;
              spawnState.burst = {
                count: sysData.burst.count,
                remaining: sysData.burst.repeat,
                interval: sysData.burst.interval,
                delay: sysData.burst.delay,
                timer: hasDelay ? sysData.burst.delay : 0,
                delayPassed: !hasDelay,
              };
              spawnState._originalRemaining = sysData.burst.repeat;
            }
            break;
        }
        
        this.systemSpawnStates.set(sys, spawnState);
      }
    }
  }
  
  private _clearEventCount(): void {
    // Reset shared event count for next frame
    const eventCountData = new Uint32Array([0]);
    this.ctx.updateBuffer(this.eventCountBuffer!, eventCountData);
  }
  
  /**
   * Re-arm finite spawn states without resetting particles.
   * Existing alive particles continue naturally; only spawn scheduling restarts.
   */
  private _rearmSpawnStates(): void {
    for (const [, state] of this.systemSpawnStates) {
      if (!state.burst) continue;
      
      if (state.type === 'single_burst') {
        state.burst.remaining = 1;
        state.burst.timer = state.burst.delay;
        state.burst.delayPassed = state.burst.delay <= 0;
      } else if (state.type === 'burst') {
        // Re-arm with original repeat count (stored in interval pattern)
        // We need the original data — store it at creation time
        if (state._originalRemaining !== undefined) {
          state.burst.remaining = state._originalRemaining;
        }
        state.burst.timer = state.burst.delay > 0 ? state.burst.delay : 0;
        state.burst.delayPassed = state.burst.delay <= 0;
      }
    }
  }
}
